/**
 * Usage analytics capture: one Redux middleware that turns user actions into
 * analytics events, so feature components need no tracking calls.
 * See features/usage_analytics.md.
 *
 * Rules that keep the numbers honest:
 *  - `hydrateWorkspace` (workspace load + live sync from another device)
 *    never emits — restoring state is not user activity.
 *  - "Opened" events fire on the closed→open edge of the state, so a repeat
 *    dispatch of an already-open view counts once.
 *  - Strategy selection is read from the two user actions only: a server
 *    snapshot also prunes `selectedIds`, and that is not a deselect.
 *  - An action dispatched by code rather than a click carries
 *    `meta.analytics = "auto"` (see `autoAction`) and is tagged `source: auto`.
 *  - Everything done while a guided tour is on screen is tagged `tour: true`.
 */

import type { Middleware, UnknownAction } from "@reduxjs/toolkit";
import type { RootState } from "@/state/store";
import { hydrateWorkspace } from "@/state/slices/workspacesSlice";
import {
  addTab,
  removeTab,
  renameTab,
  setActiveTab,
  setSplitTab,
  toggleTabLock,
} from "@/state/slices/tabsSlice";
import {
  addDiagonalLine,
  addFib,
  addHorizontalLine,
  addPosition,
  addPriceRangeBox,
  addTextLabel,
  addVerticalLine,
  commitPathDraft,
  setChartType,
  setDrawingTool,
  setIndicators,
  setSignalOrdersOpen,
  setSymbol,
  setTimeframe,
  type IndicatorSettings,
} from "@/state/slices/uiSlice";
import { SIGNAL_TAB_ID } from "@/state/slices/signalSlice";
import {
  STRATEGIES_TAB_ID,
  removeSelectedStrategy,
  setFocusedStrategy,
  setSelectedStrategies,
  setStrategiesSymbol,
  setStrategyOrdersOpen,
} from "@/state/slices/strategiesSlice";
import {
  enterReplay,
  exitReplay,
  setReplayPlaying,
} from "@/state/slices/replaySlice";
import { selectEffectiveIndicators } from "@/state/selectors/effectiveIndicators";
import { replayEndIndex } from "@/utils/replayCursor";
import { syncDwell } from "./dwell";
import { track, type EventProps } from "./queue";

/** Mark an action as dispatched by code, not by the user. */
export function autoAction<A extends UnknownAction>(action: A): A {
  return { ...action, meta: { ...(action.meta as object | undefined), analytics: "auto" } };
}

type UiFlag = {
  [K in keyof RootState["ui"]]: RootState["ui"][K] extends boolean ? K : never;
}[keyof RootState["ui"]];

// Panel flag in the ui slice → the `panel` name reported.
const PANELS: Partial<Record<UiFlag, string>> = {
  journalOpen: "journal",
  notesOpen: "notes",
  newsOpen: "news",
  helpOpen: "help",
  weeklyBehaviourDialogOpen: "weekly_behaviour",
  oneClickPanelOpen: "one_click",
  tradingPanelOpen: "trading",
  objectsTreeOpen: "objects_tree",
  chartColorsDialogOpen: "chart_colors",
  fibLevelsDialogOpen: "fib_levels",
  terminalOpen: "terminal",
};

// Charts whose symbol is set by code, not the user: the Strategies chart
// mirrors the strategy bar's symbol (tracked as strategies_symbol_changed)
// and the Signal chart is locked to the signal account's symbol.
const CODE_SET_SYMBOL = new Set([STRATEGIES_TAB_ID, SIGNAL_TAB_ID]);

const DRAWINGS: Array<[{ match: (a: unknown) => boolean }, string]> = [
  [addFib, "fibonacci"],
  [addHorizontalLine, "horizontal"],
  [addVerticalLine, "vertical"],
  [addDiagonalLine, "diagonal"],
  [addPriceRangeBox, "price_range"],
  [addTextLabel, "text"],
];

function enabledIndicators(s: IndicatorSettings): Set<string> {
  const on = new Set<string>();
  if (s.maLines.some((m) => m.enabled)) on.add("ma");
  if (s.rsi.enabled) on.add("rsi");
  if (s.anchorRsi.enabled) on.add("anchor_rsi");
  return on;
}

function capture(action: UnknownAction, prev: RootState, cur: RootState): void {
  const common: EventProps = {};
  if (cur.tour.active) common.tour = true;
  if ((action.meta as { analytics?: string } | undefined)?.analytics === "auto") {
    common.source = "auto";
  }
  const emit = (event: string, props?: EventProps) => track(event, { ...common, ...props });

  // --- Strategies ---
  const ps = prev.strategies;
  const cs = cur.strategies;
  if (ps !== cs) {
    if (cs.viewOpen && !ps.viewOpen) {
      emit("strategies_opened", { selected: cs.selectedIds.length });
      cs.selectedIds.forEach((id) => emit("strategy_viewed", { strategy: id }));
    }
    if (cs.pickerOpen && !ps.pickerOpen) emit("strategy_picker_opened");
    if (setSelectedStrategies.match(action) || removeSelectedStrategy.match(action)) {
      const before = new Set(ps.selectedIds);
      const after = new Set(cs.selectedIds);
      cs.selectedIds.forEach((id) => {
        if (before.has(id)) return;
        emit("strategy_selected", { strategy: id });
        if (cs.viewOpen) emit("strategy_viewed", { strategy: id });
      });
      ps.selectedIds.forEach((id) => {
        if (!after.has(id)) emit("strategy_deselected", { strategy: id });
      });
    }
    if (setFocusedStrategy.match(action) && cs.focusedId && cs.focusedId !== ps.focusedId) {
      emit("strategy_focused", { strategy: cs.focusedId });
    }
    if (setStrategiesSymbol.match(action) && cs.symbol !== ps.symbol) {
      emit("strategies_symbol_changed", { symbol: cs.symbol });
    }
    // Opening the view also opens the drawer; only the user's own toggle counts.
    if (setStrategyOrdersOpen.match(action) && cs.ordersOpen && !ps.ordersOpen) {
      emit("strategy_orders_opened");
    }
  }

  // --- Signal chart + panels ---
  const pu = prev.ui;
  const cu = cur.ui;
  if (pu !== cu) {
    if (cu.signalChartOpen && !pu.signalChartOpen) emit("signal_chart_opened");
    if (setSignalOrdersOpen.match(action) && cu.signalOrdersOpen && !pu.signalOrdersOpen) {
      emit("signal_orders_opened");
    }
    for (const [flag, panel] of Object.entries(PANELS) as Array<[UiFlag, string]>) {
      if (cu[flag] && !pu[flag]) emit("panel_opened", { panel });
    }
  }

  // --- Replay ---
  // How far a replay actually ran is the interesting number: entering and
  // stepping once is a different act from walking 400 bars. `bars` is
  // measured on exit, from where the cursor ended up against where it began.
  if (enterReplay.match(action)) {
    emit("replay_started", {
      timeframe: cur.ui.byTab[action.payload.tabId]?.timeframe,
      entry: action.payload.entry,
    });
  } else if (setReplayPlaying.match(action) && action.payload.playing) {
    emit("replay_played", {
      speed: cur.replay.byTab[action.payload.tabId]?.speed,
    });
  } else if (exitReplay.match(action)) {
    const rt = action.payload.tabId;
    // Read from `prev`: exitReplay has already cleared the cursor in `cur`.
    const r = prev.replay.byTab[rt];
    const candles = prev.ohlc.byTab[rt]?.candles;
    const bars =
      r?.cursorTime != null && r.startCursorTime != null && candles?.length
        ? replayEndIndex(candles, r.cursorTime) -
          replayEndIndex(candles, r.startCursorTime)
        : undefined;
    emit("replay_exited", bars == null ? undefined : { bars });
  }

  // --- Tabs ---
  if (addTab.match(action)) emit("tab_added");
  else if (removeTab.match(action)) emit("tab_closed");
  else if (setActiveTab.match(action) && cur.tabs.activeTabId !== prev.tabs.activeTabId) {
    emit("tab_switched");
  } else if (setSplitTab.match(action) && cur.tabs.splitTabId && !prev.tabs.splitTabId) {
    emit("split_view");
  } else if (renameTab.match(action)) emit("tab_renamed");
  else if (toggleTabLock.match(action)) emit("tab_locked");

  // --- Chart, drawing, indicators (per tab) ---
  const tabId = (action.payload as { tabId?: string } | undefined)?.tabId;
  const pt = tabId ? pu.byTab[tabId] : undefined;
  const ct = tabId ? cu.byTab[tabId] : undefined;
  if (!tabId || !ct) return;

  if (setSymbol.match(action)) {
    if (pt && ct.symbol !== pt.symbol && !CODE_SET_SYMBOL.has(tabId)) {
      emit("symbol_changed", { symbol: ct.symbol });
    }
  } else if (setTimeframe.match(action) && pt && ct.timeframe !== pt.timeframe) {
    emit("timeframe_changed", { timeframe: ct.timeframe });
  } else if (setChartType.match(action) && pt && ct.chartType !== pt.chartType) {
    emit("chart_type_changed", { chart_type: ct.chartType });
  } else if (setDrawingTool.match(action)) {
    const tool = action.payload.tool;
    if (tool && tool !== pt?.activeDrawingTool) emit("tool_selected", { tool });
  } else if (addPosition.match(action)) {
    emit("drawing_created", { kind: action.payload.position.type });
  } else if (commitPathDraft.match(action)) {
    // Drafts under two points are discarded, not created.
    if (ct.paths.length > (pt?.paths.length ?? 0)) emit("drawing_created", { kind: "path" });
  } else if (setIndicators.match(action)) {
    const before = enabledIndicators(selectEffectiveIndicators(prev, tabId));
    const after = enabledIndicators(selectEffectiveIndicators(cur, tabId));
    after.forEach((i) => before.has(i) || emit("indicator_added", { indicator: i }));
    before.forEach((i) => after.has(i) || emit("indicator_removed", { indicator: i }));
  } else {
    const drawn = DRAWINGS.find(([creator]) => creator.match(action));
    if (drawn) emit("drawing_created", { kind: drawn[1] });
  }
}

/** Time-on-view: the signal chart, the Strategies view, and each strategy
 *  shown on it. Driven by state, so it holds however the view was opened. */
function dwellFor(cur: RootState): Map<string, { event: string; props: EventProps }> {
  const wanted = new Map<string, { event: string; props: EventProps }>();
  if (cur.ui.signalChartOpen) wanted.set("signal", { event: "signal_chart_dwell", props: {} });
  const s = cur.strategies;
  if (s.viewOpen) {
    wanted.set("strategies", { event: "strategies_dwell", props: {} });
    s.selectedIds.forEach((id) =>
      wanted.set(`strategy:${id}`, { event: "strategy_dwell", props: { strategy: id } }),
    );
  }
  return wanted;
}

// Typed without RootState: the store's type is inferred from its middleware
// list, so naming RootState here would make the store type circular.
export const analyticsMiddleware: Middleware =
  (api) => (next) => (action) => {
    const prev = api.getState() as RootState;
    const result = next(action);
    const cur = api.getState() as RootState;
    if (prev === cur) return result;
    try {
      if (!hydrateWorkspace.match(action)) capture(action as UnknownAction, prev, cur);
      if (prev.ui.signalChartOpen !== cur.ui.signalChartOpen || prev.strategies !== cur.strategies) {
        syncDwell(dwellFor(cur));
      }
    } catch {
      /* analytics must never break a dispatch */
    }
    return result;
  };
